import React, { useState, useEffect, useMemo } from "react";
import { Collapse, Button, Empty, Pagination, Input, Tag, Tabs, Spin, Modal, Segmented, Dropdown, Image, Drawer, Badge, Checkbox } from "antd";
import {
  WhatsAppOutlined,
  SearchOutlined,
  FilterOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined,
  EyeOutlined,
  UploadOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  CopyOutlined,
  DownloadOutlined,
  CloudUploadOutlined,
  LinkOutlined,
  DeleteOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  _get_admin_ambassadors,
  _approve_ambassador,
  _reject_ambassador,
  _suspend_ambassador,
  _unsuspend_ambassador,
  _get_ambassador_schools,
  _update_ambassador_schools,
  _get_admin_ambassador_posts,
  _update_ambassador_post_status,
  _get_ambassador_next_post,
  _update_ambassador_next_post,
  _upload_any_file,
  _get_ambassador_post_templates,
  _set_active_ambassador_post_template,
  _delete_ambassador_post_template,
} from "../../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";
import { buildAdminCollapseItem, AdminCollapseList } from "../../../components/admin/AdminCollapseItem";
import AdminTabs from "../../../components/admin/AdminTabs";
import AdminDetailsModal from "../../../components/admin/AdminDetailsModal";
import AdminAmbassadorPromos from "./AdminAmbassadorPromos";
import AdminAmbassadorLessons from "./AdminAmbassadorLessons";

const AdminAmbassadors = ({ showHeader = true, pendingOnly = false }) => {
  const [activeMainTab, setActiveMainTab] = useState("AMBASSADORS");
  const [loading, setLoading] = useState(true);
  const [ambassadors, setAmbassadors] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [studentFilter, setStudentFilter] = useState("ALL");
  const [ambassadorCategory, setAmbassadorCategory] = useState("WAITING_APPROVAL");

  useEffect(() => {
    if (pendingOnly) {
      setActiveMainTab("AMBASSADORS");
      setAmbassadorCategory("WAITING_APPROVAL");
    }
  }, [pendingOnly]);
  const [pointsSort, setPointsSort] = useState("NONE");
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [selectedAmbassador, setSelectedAmbassador] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);

  const [schoolsModalVisible, setSchoolsModalVisible] = useState(false);
  const [schoolsList, setSchoolsList] = useState([]);
  const [newSchoolInput, setNewSchoolInput] = useState("");
  const [savingSchools, setSavingSchools] = useState(false);

  const [postsModalVisible, setPostsModalVisible] = useState(false);
  const [submittedPostsList, setSubmittedPostsList] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [postStatusFilter, setPostStatusFilter] = useState("ALL");
  const [updatingPostId, setUpdatingPostId] = useState(null);

  const [postDetailsModalVisible, setPostDetailsModalVisible] = useState(false);
  const [selectedPostTemplateDetails, setSelectedPostTemplateDetails] = useState(null);

  const DEFAULT_INSTRUCTIONS = `1. Download the media asset\n2. Copy caption with your referral link\n3. Share on Instagram/TikTok/WhatsApp Status\n4. Submit post link for verification`;

  const DEFAULT_CAPTION = `Check out Konnect! Download the app and get instant cashback. Sign up using my link: {REFERRAL_LINK}`;
  const CAPTION_CHANNELS = [
    { key: "WHATSAPP", label: "WhatsApp" },
    { key: "INSTAGRAM", label: "Instagram" },
    { key: "TIKTOK", label: "TikTok" },
    { key: "TWITTER", label: "X / Twitter" },
    { key: "YOUTUBE", label: "YouTube" },
  ];

  const getTomorrowTitle = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return `Post Task - ${tomorrow.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}`;
  };

  const [nextPostModalVisible, setNextPostModalVisible] = useState(false);
  const [nextPostData, setNextPostData] = useState({
    title: getTomorrowTitle(),
    mediaUrl: "",
    mediaUrls: [],
    mediaType: "IMAGE",
    caption: DEFAULT_CAPTION,
    captions: {},
    useSameCaption: true,
    instructions: DEFAULT_INSTRUCTIONS,
  });
  const [savingNextPost, setSavingNextPost] = useState(false);
  const [uploadingMediaFile, setUploadingMediaFile] = useState(false);
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingMediaFile(true);
    try {
      const res = await _upload_any_file(file);
      const url = res.data?.url || res.data?.fileUrl || res.data?.data?.url || res.data;
      if (typeof url === "string" && url) {
        const isVideo = file.type?.startsWith("video/") || url.endsWith(".mp4") || url.endsWith(".mov") || url.endsWith(".webm");
        setNextPostData((prev) => ({
          ...prev,
          mediaUrl: url,
          mediaUrls: Array.from(new Set([...(prev.mediaUrls || []), url])),
          mediaType: isVideo ? "VIDEO" : "IMAGE",
        }));
        showSuccessNotification("Media asset uploaded successfully!");
      } else {
        showErrorNotification("Failed to upload media file.");
      }
    } catch (err) {
      showErrorNotification("Failed to upload media file.");
    } finally {
      setUploadingMediaFile(false);
    }
  };

  const [createdPostTemplates, setCreatedPostTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const fetchPostTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await _get_ambassador_post_templates();
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.templates || res.data?.data?.templates || res.data?.data || []);
      if (Array.isArray(list)) {
        setCreatedPostTemplates(list);
      }
    } catch (err) {
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectTemplateForEdit = (template) => {
    setNextPostData({
      id: template.id,
      title: template.title || getTomorrowTitle(),
      mediaUrl: template.mediaUrl || "",
      mediaType: template.mediaType || "IMAGE",
      caption: template.caption || DEFAULT_CAPTION,
      captions: template.captions || {},
      useSameCaption: !template.captions || Object.keys(template.captions || {}).length === 0,
      instructions: template.instructions || DEFAULT_INSTRUCTIONS,
      isActive: template.isActive !== undefined ? template.isActive : true,
    });
    // showSuccessNotification(`Loaded "${template.title}" in editor.`);
  };

  const handleSetActiveTemplate = async (id) => {
    try {
      const res = await _set_active_ambassador_post_template(id);
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.templates || res.data?.data?.templates || []);
      if (Array.isArray(list) && list.length > 0) {
        setCreatedPostTemplates(list);
      } else {
        await fetchPostTemplates();
      }
      if (res.data?.activeTemplate || res.data?.data?.activeTemplate) {
        setNextPostData(res.data?.activeTemplate || res.data?.data?.activeTemplate);
      }
      showSuccessNotification("Post task activated live for ambassadors!");
    } catch (err) {
      showErrorNotification("Failed to activate post task.");
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Are you sure you want to delete this post task?")) return;
    try {
      const res = await _delete_ambassador_post_template(id);
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.templates || res.data?.data?.templates || []);
      if (Array.isArray(list)) {
        setCreatedPostTemplates(list);
      } else {
        await fetchPostTemplates();
      }
      showSuccessNotification("Post task deleted.");
    } catch (err) {
      showErrorNotification("Failed to delete post task.");
    }
  };

  const handlePrepareNewTemplate = () => {
    setNextPostData({
      id: undefined,
      title: getTomorrowTitle(),
      mediaUrl: "",
      mediaType: "IMAGE",
      caption: DEFAULT_CAPTION,
      captions: {},
      useSameCaption: true,
      instructions: DEFAULT_INSTRUCTIONS,
      isActive: true,
    });
  };

  const fetchNextPost = async () => {
    try {
      const res = await _get_ambassador_next_post();
      const data = res.data?.data || res.data;
      if (data) {
        setNextPostData({
          id: data.id,
          title: data.title || getTomorrowTitle(),
          mediaUrl: data.mediaUrl || "",
          mediaType: data.mediaType || "IMAGE",
          caption: data.caption || DEFAULT_CAPTION,
          captions: data.captions || {},
          useSameCaption: !data.captions || Object.keys(data.captions || {}).length === 0,
          instructions: data.instructions || DEFAULT_INSTRUCTIONS,
          isActive: data.isActive !== undefined ? data.isActive : true,
        });
      }
    } catch (err) {
      showErrorNotification("Failed to fetch Next Post details.");
    }
  };

  const handleSaveNextPost = async () => {
    setSavingNextPost(true);
    try {
      const captions = nextPostData.useSameCaption
        ? { GENERAL: nextPostData.caption || DEFAULT_CAPTION }
        : Object.fromEntries(
          Object.entries(nextPostData.captions || {}).filter(([, value]) => value && value.trim())
        );
      const res = await _update_ambassador_next_post({
        ...nextPostData,
        captions,
        caption: nextPostData.caption || captions.GENERAL || DEFAULT_CAPTION,
      });
      showSuccessNotification("Next Post asset & captions saved successfully!");
      const list = Array.isArray(res.data)
        ? res.data
        : (res.data?.templates || res.data?.data?.templates || []);
      if (Array.isArray(list) && list.length > 0) {
        setCreatedPostTemplates(list);
      } else {
        await fetchPostTemplates();
      }
      setNextPostModalVisible(false);
    } catch (err) {
      showErrorNotification("Failed to save Next Post details.");
    } finally {
      setSavingNextPost(false);
    }
  };

  const fetchSubmittedPosts = async (filter = "ALL") => {
    setLoadingPosts(true);
    try {
      const res = await _get_admin_ambassador_posts(filter !== "ALL" ? filter : undefined);
      const list = res.data?.data || res.data || [];
      if (Array.isArray(list)) {
        setSubmittedPostsList(list);
      }
    } catch (err) {
      showErrorNotification("Failed to fetch submitted posts list.");
    } finally {
      setLoadingPosts(false);
    }
  };

  const handleUpdatePostStatus = async (postId, status) => {
    let note = undefined;
    if (status === "REJECTED") {
      note = prompt("Optional rejection reason for ambassador:");
      if (note === null) return;
    }
    setUpdatingPostId(postId);
    try {
      await _update_ambassador_post_status(postId, status, note);
      showSuccessNotification(`Post marked as ${status}!`);
      fetchSubmittedPosts(postStatusFilter);
    } catch (err) {
      showErrorNotification(err?.response?.data?.message || "Failed to update post status.");
    } finally {
      setUpdatingPostId(null);
    }
  };

  const fetchSchools = async () => {
    try {
      const res = await _get_ambassador_schools();
      const list = res.data?.data || res.data || [];
      if (Array.isArray(list)) {
        setSchoolsList(list.filter((school) => school !== "Other"));
      }
    } catch (err) {
      showErrorNotification("Failed to fetch Ambassador schools list.");
    }
  };

  const handleSaveSchools = async () => {
    setSavingSchools(true);
    try {
      await _update_ambassador_schools(schoolsList);
      showSuccessNotification("Ambassador schools list updated successfully!");
      setSchoolsModalVisible(false);
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Failed to update schools list.");
    } finally {
      setSavingSchools(false);
    }
  };

  const fetchAmbassadors = async () => {
    setLoading(true);
    try {
      const res = await _get_admin_ambassadors();
      if (res.data) {
        const list = Array.isArray(res.data) ? res.data : res.data.data || [];
        setAmbassadors(list.filter((amb) => amb.status !== "REJECTED"));
      }
    } catch (err) {
      showErrorNotification("Failed to fetch Ambassadors list.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    const amb = ambassadors.find((a) => a.id === id);
    if (amb) {
      if (!amb.trainingCompleted) {
        showErrorNotification(
          "Ambassador cannot be approved until they complete all orientation lessons and final quiz."
        );
        return;
      }
    }
    setActionLoadingId(id);
    try {
      const res = await _approve_ambassador(id);
      if (res.data?.success || res.status === 200) {
        showSuccessNotification("Ambassador application approved successfully!");
        fetchAmbassadors();
      } else {
        showErrorNotification(res.data?.message || "Failed to approve Ambassador.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Failed to approve Ambassador.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm("Are you sure you want to reject this Ambassador? This will remove them from the Ambassador list.")) {
      return;
    }
    setActionLoadingId(id);
    try {
      const res = await _reject_ambassador(id);
      if (res.data?.success || res.status === 200) {
        showSuccessNotification("Ambassador rejected and removed from list.");
        setAmbassadors((prev) => prev.filter((a) => a.id !== id));
        fetchAmbassadors();
      } else {
        showErrorNotification(res.data?.message || "Failed to reject Ambassador.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Failed to reject Ambassador.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleSuspend = async (id) => {
    if (!window.confirm("Are you sure you want to suspend this Ambassador? Their task submissions and reward payouts will be disabled.")) {
      return;
    }
    setActionLoadingId(id);
    try {
      const res = await _suspend_ambassador(id);
      if (res.data?.success || res.status === 200) {
        showSuccessNotification("Ambassador account suspended successfully.");
        fetchAmbassadors();
      } else {
        showErrorNotification(res.data?.message || "Failed to suspend Ambassador.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Failed to suspend Ambassador.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleUnsuspend = async (id) => {
    setActionLoadingId(id);
    try {
      const res = await _unsuspend_ambassador(id);
      if (res.data?.success || res.status === 200) {
        showSuccessNotification("Ambassador unsuspended and activated successfully.");
        fetchAmbassadors();
      } else {
        showErrorNotification(res.data?.message || "Failed to unsuspend Ambassador.");
      }
    } catch (err) {
      showErrorNotification(err.response?.data?.message || "Failed to unsuspend Ambassador.");
    } finally {
      setActionLoadingId(null);
    }
  };

  useEffect(() => {
    fetchAmbassadors();
    fetchPostTemplates();
  }, []);

  useEffect(() => {
    if (activeMainTab === "POSTS") {
      fetchPostTemplates();
    }
  }, [activeMainTab]);

  const getAmbassadorPoints = (amb) => {
    return Number(amb.points || 0);
  };

  const isStudentAmbassador = (amb) =>
    Boolean(amb.school && amb.school !== "—") ||
    amb.isStudent === true ||
    String(amb.isStudent) === "1" ||
    String(amb.isStudent) === "true";

  const getAmbassadorStatus = (amb) => {
    if (amb.isAmbassador) return "APPROVED";
    if (amb.status === "SUSPENDED") return "SUSPENDED";
    if (amb.status === "REJECTED") return "REJECTED";
    if (amb.trainingCompleted) return "WAITING_APPROVAL";
    return "PENDING";
  };

  const filteredAmbassadors = useMemo(() => {
    let result = ambassadors.filter((amb) => {
      const s = getAmbassadorStatus(amb);
      if (s === "REJECTED") return false;
      if (ambassadorCategory === "WAITING_APPROVAL") return s === "WAITING_APPROVAL" || s === "WAITING_FOR_APPROVAL";
      if (ambassadorCategory === "PENDING") return s === "PENDING";
      if (ambassadorCategory === "APPROVED") return s === "APPROVED";
      if (ambassadorCategory === "SUSPENDED") return s === "SUSPENDED";
      if (ambassadorCategory === "STUDENTS") return isStudentAmbassador(amb);
      if (ambassadorCategory === "NON_STUDENTS") return !isStudentAmbassador(amb);
      return true;
    });
    if (statusFilter !== "ALL") {
      result = result.filter((amb) => {
        const s = getAmbassadorStatus(amb);
        if (statusFilter === "WAITING_APPROVAL" || statusFilter === "WAITING_FOR_APPROVAL") {
          return s === "WAITING_APPROVAL" || s === "WAITING_FOR_APPROVAL";
        }
        return s === statusFilter.toUpperCase();
      });
    }
    if (studentFilter !== "ALL") {
      result = result.filter((amb) => {
        const isStud = isStudentAmbassador(amb);
        return studentFilter === "STUDENT" ? isStud : !isStud;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((amb) => {
        const fullName = `${amb.firstName || ""} ${amb.lastName || ""}`.toLowerCase();
        const email = (amb.email || "").toLowerCase();
        const whatsapp = (amb.ambassadorWhatsapp || amb.phoneNumber || "").toLowerCase();
        const code = (amb.referralCode || "").toLowerCase();
        const accId = (amb.accountId || "").toLowerCase();
        return (
          fullName.includes(q) ||
          email.includes(q) ||
          whatsapp.includes(q) ||
          code.includes(q) ||
          accId.includes(q)
        );
      });
    }

    if (pointsSort === "POINTS_DESC") {
      result.sort((a, b) => getAmbassadorPoints(b) - getAmbassadorPoints(a));
    } else if (pointsSort === "POINTS_ASC") {
      result.sort((a, b) => getAmbassadorPoints(a) - getAmbassadorPoints(b));
    }

    return result;
  }, [ambassadors, ambassadorCategory, searchQuery, statusFilter, studentFilter, pointsSort]);

  const approvedCount = useMemo(() => {
    return ambassadors.filter((amb) => getAmbassadorStatus(amb) === "APPROVED").length;
  }, [ambassadors]);

  const waitingApprovalCount = useMemo(() => {
    return ambassadors.filter((amb) => {
      const s = getAmbassadorStatus(amb);
      return s === "WAITING_APPROVAL" || s === "WAITING_FOR_APPROVAL";
    }).length;
  }, [ambassadors]);

  const pendingCount = useMemo(() => {
    return ambassadors.filter((amb) => getAmbassadorStatus(amb) === "PENDING").length;
  }, [ambassadors]);

  const suspendedCount = useMemo(() => {
    return ambassadors.filter((amb) => getAmbassadorStatus(amb) === "SUSPENDED").length;
  }, [ambassadors]);

  const studentCount = useMemo(
    () => ambassadors.filter((amb) => getAmbassadorStatus(amb) !== "REJECTED" && isStudentAmbassador(amb)).length,
    [ambassadors],
  );

  const nonStudentCount = useMemo(
    () => ambassadors.filter((amb) => getAmbassadorStatus(amb) !== "REJECTED" && !isStudentAmbassador(amb)).length,
    [ambassadors],
  );

  const paginatedAmbassadors = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAmbassadors.slice(start, start + pageSize);
  }, [filteredAmbassadors, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const getContactPhone = (amb) => {
    return amb.ambassadorWhatsapp || amb.phoneNumber || "";
  };

  const handleOpenDetails = (amb) => {
    setSelectedAmbassador(amb);
    setDetailsModalVisible(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalVisible(false);
    setSelectedAmbassador(null);
  };

  const renderCollapseItems = () => {
    return paginatedAmbassadors.map((amb, index) => {
      const phone = getContactPhone(amb);
      const rawStatus = getAmbassadorStatus(amb);
      const isWaiting = rawStatus === "WAITING_APPROVAL" || rawStatus === "WAITING_FOR_APPROVAL";
      const statusLabel = isWaiting
        ? "Waiting for Approval"
        : rawStatus === "APPROVED"
          ? "Approved"
          : rawStatus === "PENDING"
            ? "Pending"
            : rawStatus;
      const statusColor = rawStatus === "APPROVED" ? "green" : isWaiting ? "gold" : rawStatus === "PENDING" ? "blue" : "orange";

      const isTier2 = amb.tier === "tier_2" || amb.tier === "tier_3" || amb.tier === "2";

      const totalLessons = Number(amb.totalLessons) || (Array.isArray(amb.lessons) ? amb.lessons.length : 0);
      const isApproved = amb.isAmbassador || rawStatus === "APPROVED";
      let watchedCount = amb.lessonsCompletedCount;
      if (isApproved || amb.trainingCompleted) {
        watchedCount = totalLessons;
      } else if (amb.lessonsCompleted) {
        try {
          const parsed = typeof amb.lessonsCompleted === "string" ? JSON.parse(amb.lessonsCompleted) : amb.lessonsCompleted;
          if (parsed && typeof parsed === "object") {
            const numericKeys = Object.keys(parsed).filter((k) => !isNaN(Number(k)) && parsed[k]);
            const nonNumericKeys = Object.keys(parsed).filter((k) => isNaN(Number(k)) && !k.startsWith('quiz_') && parsed[k]);
            const parsedCount = Math.max(numericKeys.length, nonNumericKeys.length);
            if (!watchedCount || parsedCount > watchedCount) {
              watchedCount = parsedCount;
            }
          }
        } catch (e) { }
      }
      watchedCount = (isApproved || amb.trainingCompleted)
        ? (totalLessons || Number(watchedCount) || 0)
        : (totalLessons > 0 ? Math.min(totalLessons, Math.max(0, Number(watchedCount) || 0)) : Math.max(0, Number(watchedCount) || 0));

      const items = [
        {
          fullWidth: true,
          label: "Onboarding Progress",
          value: (
            <div className="grid grid-cols-3 gap-2 bg-gradient-to-r from-slate-50 to-indigo-50/40 border border-slate-200/80 rounded-xl p-2 w-full text-center mt-1">
              <div className="bg-white py-2 px-1 rounded-lg border border-gray-100 shadow-xs flex flex-col items-center justify-center min-w-0">
                <span className="text-[10px] sm:text-[11px] font-gilroyBold uppercase text-gray-400 block mb-1 truncate w-full">
                  Wallet Tier
                </span>
                <Tag
                  color={isTier2 ? "green" : "blue"}
                  className="font-gilroyBold m-0 text-[11px] sm:text-[12px] px-1.5 py-0.5 rounded-md border-0 leading-tight text-center truncate max-w-full"
                >
                  {isTier2 ? "Tier 2" : "Tier 1"}
                </Tag>
              </div>
              <div className="bg-white py-2 px-1 rounded-lg border border-gray-100 shadow-xs flex flex-col items-center justify-center min-w-0">
                <span className="text-[10px] sm:text-[11px] font-gilroyBold uppercase text-gray-400 block mb-1 truncate w-full">
                  Lessons
                </span>
                <Tag
                  color={amb.trainingCompleted ? "green" : "blue"}
                  className="font-gilroyBold m-0 text-[11px] sm:text-[12px] px-1.5 py-0.5 rounded-md border-0 leading-tight text-center truncate max-w-full"
                >
                  {totalLessons > 0
                    ? (amb.trainingCompleted ? `${totalLessons}/${totalLessons} Done` : `${watchedCount}/${totalLessons} Done`)
                    : (amb.trainingCompleted ? "Done" : `${watchedCount} Done`)}
                </Tag>
              </div>
              <div className="bg-white py-2 px-1 rounded-lg border border-gray-100 shadow-xs flex flex-col items-center justify-center min-w-0">
                <span className="text-[10px] sm:text-[11px] font-gilroyBold uppercase text-gray-400 block mb-1 truncate w-full">
                  Quiz
                </span>
                <Tag
                  color={(amb.quizScore || 0) >= 4 ? "purple" : (amb.quizScore || 0) > 0 ? "blue" : amb.trainingCompleted ? "green" : "default"}
                  className="font-gilroyBold m-0 text-[11px] sm:text-[12px] px-1.5 py-0.5 rounded-md border-0 leading-tight text-center truncate max-w-full"
                >
                  {amb.quizScore !== undefined && amb.quizScore !== null && Number(amb.quizScore) > 0
                    ? `${amb.quizScore} Pts`
                    : amb.trainingCompleted
                      ? "Completed"
                      : "0 Pts"}
                </Tag>
              </div>
            </div>
          ),
        },
        {
          label: "Status",
          value: (
            <Tag color={statusColor} className="font-gilroyBold text-[12px] px-2.5 py-0.5 rounded-md border-0 m-0">
              {statusLabel}
            </Tag>
          ),
        },
        {
          label: "Full Name",
          value: (
            <span className="font-gilroyBold text-gray-900 text-[13px]">
              {amb.firstName} {amb.lastName}
            </span>
          ),
        },
        {
          label: "Email",
          value: <span className="text-[13px] text-gray-700">{amb.email}</span>,
        },
        {
          label: "Telephone Number",
          value: <span className="text-[13px] text-gray-700">{amb.phoneNumber || "—"}</span>,
        },
        {
          label: "WhatsApp Number",
          value: <span className="text-[13px] text-gray-700">{amb.ambassadorWhatsapp || phone || "—"}</span>,
        },
        {
          label: "Types of Referrals",
          value: (
            <span className="text-[13px] text-gray-700">
              USER: <strong className="text-gray-900">{amb.referralsBreakdown?.user || 0}</strong> | BUSINESS: <strong className="text-gray-900">{amb.referralsBreakdown?.business || 0}</strong> | RIDER: <strong className="text-gray-900">{amb.referralsBreakdown?.rider || 0}</strong> | KA: <strong className="text-gray-900">{amb.referralsBreakdown?.ka || 0}</strong>
            </span>
          ),
        },
        {
          label: "Points / Score",
          value: (
            <Tag color="purple" className="font-gilroyBold text-[12px] px-2.5 py-0.5 rounded-md border-0 m-0">
              {getAmbassadorPoints(amb).toLocaleString()} Points
            </Tag>
          ),
        },
        {
          label: "Income / Week",
          value: (
            <span className="text-[13px] text-gray-700">
              REFERRAL: <strong className="text-gray-900">₦{parseFloat(amb.weeklyIncome?.referralIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> | SALES: <strong className="text-gray-900">₦{parseFloat(amb.weeklyIncome?.salesIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> | TOTAL: <strong className="text-gray-900">₦{parseFloat(amb.weeklyIncome?.totalWeeklyIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </span>
          ),
        },
      ];

      const actions = (
        <>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleOpenDetails(amb)}
            className="font-gilroyBold text-[13px] rounded-lg"
          >
            View Full Details
          </Button>
          {phone && (
            <Button
              type="default"
              size="small"
              icon={<WhatsAppOutlined />}
              onClick={() => {
                const clean = phone.replace(/\D/g, "");
                window.open(`https://wa.me/${clean}`, "_blank");
              }}
              className="font-gilroyBold text-[13px] text-green-600 border-green-600 rounded-lg"
            >
              WhatsApp Chat
            </Button>
          )}
          {rawStatus === "APPROVED" && (
            <Button
              type="primary"
              size="small"
              loading={actionLoadingId === amb.id}
              onClick={() => handleSuspend(amb.id)}
              style={{ backgroundColor: "#d97706", borderColor: "#d97706" }}
              className="font-gilroyBold text-[13px] text-white rounded-lg"
            >
              Suspend
            </Button>
          )}
          {rawStatus === "SUSPENDED" && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={actionLoadingId === amb.id}
              onClick={() => handleUnsuspend(amb.id)}
              style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
              className="font-gilroyBold text-[13px] rounded-lg"
            >
              Unsuspend
            </Button>
          )}
          {(rawStatus === "PENDING" || isWaiting) && (
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              disabled={!amb.trainingCompleted}
              title={
                !amb.trainingCompleted
                  ? "Requires completed final quizzes"
                  : "Approve Ambassador"
              }
              loading={actionLoadingId === amb.id}
              onClick={() => handleApprove(amb.id)}
              style={
                !amb.trainingCompleted
                  ? { opacity: 0.6 }
                  : { backgroundColor: "#16a34a", borderColor: "#16a34a" }
              }
              className="font-gilroyBold text-[13px] rounded-lg"
            >
              Approve
            </Button>
          )}
          <Button
            type="primary"
            danger
            size="small"
            icon={<CloseOutlined />}
            loading={actionLoadingId === amb.id}
            onClick={() => handleReject(amb.id)}
            className="font-gilroyBold text-[13px] rounded-lg"
          >
            Reject & Remove
          </Button>
        </>
      );

      return buildAdminCollapseItem({
        key: amb.id || index,
        headerTitle: `${amb.firstName} ${amb.lastName}`,
        headerSubtitle: `${isTier2 ? "Tier 2" : "Tier 1"} • ${amb.trainingCompleted ? "All Lessons Done" : totalLessons > 0 ? `${watchedCount}/${totalLessons} Lessons Watched` : `${watchedCount} Lessons Watched`} • Quiz: ${amb.quizScore !== undefined && amb.quizScore !== null && Number(amb.quizScore) > 0 ? `${amb.quizScore} pts` : amb.trainingCompleted ? "Completed" : "0 pts"}`,
        headerTagText: statusLabel,
        headerTags: [
          { text: statusLabel, color: statusColor },
          ...(amb.followersRange ? [{ text: `${amb.followersRange} followers`, color: "default" }] : []),
        ],
        items: items,
        actions: actions,
      });
    });
  };

  const renderPostCollapseItems = () => {
    return postTemplatesList.map((item, index) => {
      const collapseItems = [
        {
          label: "Status",
          value: (
            <Tag color={item.isActive ? "green" : "default"} className="font-gilroyBold uppercase">
              {item.isActive ? "LIVE ACTIVE" : "INACTIVE"}
            </Tag>
          ),
        },
        {
          label: "Media Type",
          value: <Tag className="font-mono">{item.mediaType || "IMAGE"}</Tag>,
        },
        ...(item.mediaUrl
          ? [
            {
              label: "Media Asset",
              value: (
                <div className="flex items-center justify-end">
                  {item.mediaType === "VIDEO" ? (
                    <video src={item.mediaUrl} controls className="h-12 max-w-[140px] rounded-lg border border-gray-200" />
                  ) : (
                    <Image
                      src={item.mediaUrl}
                      alt="Media Asset"
                      width={48}
                      height={48}
                      className="object-cover rounded-lg border border-gray-200 shadow-xs cursor-pointer overflow-hidden"
                    />
                  )}
                </div>
              ),
            },
          ]
          : []),
        {
          label: "Last Updated",
          value: item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "—",
        },
      ];

      const actions = (
        <>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedPostTemplateDetails(item);
              setPostDetailsModalVisible(true);
            }}
            className="font-gilroyBold rounded-lg"
          >
            View Details
          </Button>

          <Button
            type="default"
            size="small"
            onClick={() => {
              handleSelectTemplateForEdit(item);
              setNextPostModalVisible(true);
            }}
            className="font-gilroyBold rounded-lg"
          >
            Edit Task
          </Button>

          {!item.isActive && (
            <Button
              type="primary"
              size="small"
              onClick={() => handleSetActiveTemplate(item.id)}
              style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
              className="font-gilroyBold rounded-lg"
            >
              Set Live Active
            </Button>
          )}

          <Button
            type="primary"
            danger
            size="small"
            icon={<CloseOutlined />}
            onClick={() => handleDeleteTemplate(item.id)}
            className="font-gilroyBold rounded-lg"
          >
            Delete
          </Button>
        </>
      );

      return buildAdminCollapseItem({
        key: item.id || index,
        headerTitle: item.title || "Campaign Post Task",
        headerTagText: item.isActive ? "LIVE ACTIVE" : "INACTIVE",
        headerTagColor: item.isActive ? "green" : "default",
        items: collapseItems,
        actions: actions,
      });
    });
  };

  const tabItems = [
    { key: "ALL", label: <span className="font-gilroyBold text-lg">ALL</span> },
    { key: "APPROVED", label: <span className="font-gilroyBold text-lg">APPROVED</span> },
    { key: "PENDING", label: <span className="font-gilroyBold text-lg">PENDING</span> },
  ];

  const modalPhone = selectedAmbassador ? getContactPhone(selectedAmbassador) : "";
  const modalTopPosts = selectedAmbassador && Array.isArray(selectedAmbassador.topPosts) ? selectedAmbassador.topPosts : [];
  const modalStatus = selectedAmbassador ? getAmbassadorStatus(selectedAmbassador) : "";

  const modalSections = useMemo(() => {
    if (!selectedAmbassador) return [];

    const sec1 = {
      title: "Personal Information",
      bgClass: "bg-blue-50 border-blue-200",
      titleColorClass: "text-blue-900",
      labelColorClass: "text-blue-700",
      items: [
        {
          label: "Status",
          value: (
            <Tag
              color={
                modalStatus === "APPROVED"
                  ? "green"
                  : modalStatus === "WAITING_APPROVAL" || modalStatus === "WAITING_FOR_APPROVAL"
                    ? "gold"
                    : modalStatus === "PENDING"
                      ? "blue"
                      : "orange"
              }
              className="font-gilroyBold"
            >
              {modalStatus === "WAITING_APPROVAL" || modalStatus === "WAITING_FOR_APPROVAL"
                ? "Waiting for Approval"
                : modalStatus}
            </Tag>
          ),
        },
        { label: "Full Name", value: `${selectedAmbassador.firstName} ${selectedAmbassador.lastName}` },
        { label: "Email", value: selectedAmbassador.email },
        { label: "Telephone Number", value: selectedAmbassador.phoneNumber || "—" },
        { label: "WhatsApp Number", value: selectedAmbassador.ambassadorWhatsapp || modalPhone || "—" },
        { label: "Account ID", value: <span className="font-mono">{selectedAmbassador.accountId || "—"}</span> },
        { label: "Referral Code", value: <span className="font-mono">{selectedAmbassador.referralCode || "—"}</span> },
        {
          label: "Approved Date",
          value: selectedAmbassador.ambassadorApprovedAt
            ? new Date(selectedAmbassador.ambassadorApprovedAt).toLocaleDateString()
            : "—",
        },
      ],
    };

    const sec2 = {
      title: "Financial & Referral Performance",
      bgClass: "bg-green-50 border-green-200",
      titleColorClass: "text-green-900",
      labelColorClass: "text-green-700",
      items: [
        { label: "Wallet Balance", value: `₦${parseFloat(selectedAmbassador.walletBalance || 0).toFixed(2)}` },
        { label: "Sales Balance", value: `₦${parseFloat(selectedAmbassador.salesBalance || 0).toFixed(2)}` },
        {
          label: "Types of Referrals",
          value: (
            <span>
              USER: <strong>{selectedAmbassador.referralsBreakdown?.user || 0}</strong> | BUSINESS: <strong>{selectedAmbassador.referralsBreakdown?.business || 0}</strong> | RIDER: <strong>{selectedAmbassador.referralsBreakdown?.rider || 0}</strong> | KA: <strong>{selectedAmbassador.referralsBreakdown?.ka || 0}</strong>
            </span>
          ),
        },
        {
          label: "Income / Week",
          value: (
            <span>
              REFERRAL: <strong>₦{parseFloat(selectedAmbassador.weeklyIncome?.referralIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> | SALES: <strong>₦{parseFloat(selectedAmbassador.weeklyIncome?.salesIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> | TOTAL: <strong>₦{parseFloat(selectedAmbassador.weeklyIncome?.totalWeeklyIncome || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
            </span>
          ),
        },
        { label: "Followers Count", value: selectedAmbassador.followersRange || "—" },
      ],
    };

    const isSelectedTier2 =
      selectedAmbassador.tier === "tier_2" ||
      selectedAmbassador.tier === "tier_3" ||
      selectedAmbassador.tier === "2";

    const selectedTotalLessons = Number(selectedAmbassador.totalLessons) || (Array.isArray(selectedAmbassador.lessons) ? selectedAmbassador.lessons.length : 0);
    const isSelectedApproved = selectedAmbassador.isAmbassador || modalStatus === "APPROVED";
    let selectedWatchedCount = selectedAmbassador.lessonsCompletedCount;
    if (isSelectedApproved || selectedAmbassador.trainingCompleted) {
      selectedWatchedCount = selectedTotalLessons;
    } else if (selectedAmbassador.lessonsCompleted) {
      try {
        const parsed = typeof selectedAmbassador.lessonsCompleted === "string" ? JSON.parse(selectedAmbassador.lessonsCompleted) : selectedAmbassador.lessonsCompleted;
        if (parsed && typeof parsed === "object") {
          const numericKeys = Object.keys(parsed).filter((k) => !isNaN(Number(k)) && parsed[k]);
          const nonNumericKeys = Object.keys(parsed).filter((k) => isNaN(Number(k)) && !k.startsWith('quiz_') && parsed[k]);
          const parsedCount = Math.max(numericKeys.length, nonNumericKeys.length);
          if (!selectedWatchedCount || parsedCount > selectedWatchedCount) {
            selectedWatchedCount = parsedCount;
          }
        }
      } catch (e) { }
    }
    selectedWatchedCount = (isSelectedApproved || selectedAmbassador.trainingCompleted)
      ? (selectedTotalLessons || Number(selectedWatchedCount) || 0)
      : (selectedTotalLessons > 0 ? Math.min(selectedTotalLessons, Math.max(0, Number(selectedWatchedCount) || 0)) : Math.max(0, Number(selectedWatchedCount) || 0));

    const secOnboarding = {
      title: "Onboarding & Eligibility Status",
      bgClass: "bg-indigo-50 border-indigo-200",
      titleColorClass: "text-indigo-900",
      labelColorClass: "text-indigo-700",
      items: [
        {
          label: "Wallet Tier",
          value: (
            <Tag color={isSelectedTier2 ? "green" : "blue"} className="font-gilroyBold">
              {isSelectedTier2
                ? "Tier 2 Verified"
                : "Tier 1"}
            </Tag>
          ),
        },
        {
          label: "Lessons Completed",
          value: (
            <Tag
              color={selectedAmbassador.trainingCompleted ? "green" : "blue"}
              className="font-gilroyBold"
            >
              {selectedAmbassador.trainingCompleted
                ? (selectedTotalLessons > 0 ? `Completed (All ${selectedTotalLessons} Lessons)` : "Completed")
                : (selectedTotalLessons > 0 ? `${selectedWatchedCount}/${selectedTotalLessons} Lessons Watched` : `${selectedWatchedCount} Lessons Watched`)}
            </Tag>
          ),
        },
        {
          label: "Qualifying Quiz Score",
          value: (
            <Tag
              color={
                (selectedAmbassador.quizScore || 0) >= 4
                  ? "purple"
                  : (selectedAmbassador.quizScore || 0) > 0
                    ? "blue"
                    : selectedAmbassador.trainingCompleted
                      ? "green"
                      : "default"
              }
              className="font-gilroyBold"
            >
              {selectedAmbassador.quizScore !== undefined && selectedAmbassador.quizScore !== null && Number(selectedAmbassador.quizScore) > 0
                ? `${selectedAmbassador.quizScore} Points`
                : selectedAmbassador.trainingCompleted
                  ? "Completed"
                  : "Not Taken"}
            </Tag>
          ),
        },
      ],
    };

    const result = [secOnboarding, sec1, sec2];

    if (selectedAmbassador.isStudent) {
      result.push({
        title: "Student Ambassador Information",
        bgClass: "bg-emerald-50 border-emerald-200",
        titleColorClass: "text-emerald-900",
        labelColorClass: "text-emerald-700",
        items: [
          { label: "Higher Institution Student", value: <strong className="text-green-600">Yes</strong> },
          { label: "School / University", value: selectedAmbassador.school || "—" },
          { label: "Graduation Year", value: selectedAmbassador.graduationYear || "—" },
          { label: "Student Number", value: <span className="font-mono">{selectedAmbassador.studentNumber || "—"}</span> },
          ...(selectedAmbassador.recentPictureUrl
            ? [
              {
                label: "Recent Picture",
                value: (
                  <a href={selectedAmbassador.recentPictureUrl} target="_blank" rel="noopener noreferrer" className="text-green-600 underline font-semibold">
                    View Recent Picture
                  </a>
                ),
              },
            ]
            : []),
          ...(selectedAmbassador.studentIdCardUrl
            ? [
              {
                label: "Student ID Card",
                value: (
                  <a href={selectedAmbassador.studentIdCardUrl} target="_blank" rel="noopener noreferrer" className="text-green-600 underline font-semibold">
                    View Student ID Card
                  </a>
                ),
              },
            ]
            : []),
        ],
      });
    }

    result.push({
      title: "Social Media Profiles",
      bgClass: "bg-purple-50 border-purple-200",
      titleColorClass: "text-purple-900",
      labelColorClass: "text-purple-700",
      items: [
        {
          label: "Instagram Profile",
          value: selectedAmbassador.igUrl ? (
            <a href={selectedAmbassador.igUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline font-semibold">View Instagram</a>
          ) : "—",
        },
        {
          label: "TikTok Profile",
          value: selectedAmbassador.tiktokUrl ? (
            <a href={selectedAmbassador.tiktokUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline font-semibold">View TikTok</a>
          ) : "—",
        },
        {
          label: "Facebook Profile",
          value: selectedAmbassador.facebookUrl ? (
            <a href={selectedAmbassador.facebookUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline font-semibold">View Facebook</a>
          ) : "—",
        },
        ...(modalTopPosts.length > 0
          ? [
            {
              label: "Top Best Posts",
              value: (
                <div className="space-y-1">
                  {modalTopPosts.map((link, pIdx) => (
                    <div key={pIdx}>
                      <a href={link} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline font-semibold">
                        Post #{pIdx + 1}: {link}
                      </a>
                    </div>
                  ))}
                </div>
              ),
            },
          ]
          : []),
      ],
    });

    return result;
  }, [selectedAmbassador, modalPhone, modalTopPosts, modalStatus]);

  const postTemplatesList = Array.isArray(createdPostTemplates) ? createdPostTemplates : [];
  const ambassadorList = Array.isArray(ambassadors) ? ambassadors : [];

  const mainTabItems = [
    { key: "AMBASSADORS", label: <span className="font-gilroyBold sm:text-lg">Ambassadors ({ambassadorList.length})</span> },
    { key: "POSTS", label: <span className="font-gilroyBold sm:text-lg">Campaign Posts ({postTemplatesList.length})</span> },
    { key: "PROMOS", label: <span className="font-gilroyBold sm:text-lg">Promo Tasks</span> },
    { key: "LESSONS", label: <span className="font-gilroyBold sm:text-lg">Lessons & Quizzes</span> },
  ];

  return (
    <Spin spinning={loading}>
      <div className="space-y-4">
        {showHeader && (
          <div className="main_container_title_div mb-5 mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                Ambassadors
              </h1>
              <p className="text-gray-500   mt-1">
                View active ambassadors, social profiles, and performance metrics
              </p>
            </div>
            {activeMainTab === "AMBASSADORS" && (
              <Button
                type="primary"
                size="large"
                onClick={() => {
                  fetchSchools();
                  setSchoolsModalVisible(true);
                }}
                style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                className="font-gilroyBold rounded-xl h-10 px-4 flex items-center justify-center text-white shadow-xs flex-shrink-0"
              >
                Manage Schools List
              </Button>
            )}
          </div>
        )}

        {!pendingOnly && (
          <AdminTabs
            activeKey={activeMainTab}
            onChange={setActiveMainTab}
            items={mainTabItems}
          />
        )}

        {activeMainTab === "AMBASSADORS" && (
          <div className="space-y-4">
            {!pendingOnly && (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-3">
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "WAITING_APPROVAL", label: "Waiting for Approval", count: waitingApprovalCount },
                    { key: "PENDING", label: "Pending", count: pendingCount },
                    { key: "APPROVED", label: "Approved", count: approvedCount },
                    { key: "SUSPENDED", label: "Suspended", count: suspendedCount },
                    { key: "STUDENTS", label: "Students", count: studentCount },
                    { key: "NON_STUDENTS", label: "Non-Students", count: nonStudentCount },
                  ].map((category) => (
                    <Button
                      key={category.key}
                      type={ambassadorCategory === category.key ? "primary" : "default"}
                      onClick={() => {
                        setAmbassadorCategory(category.key);
                        setStatusFilter("ALL");
                        setStudentFilter("ALL");
                        setCurrentPage(1);
                      }}
                      className="font-gilroyBold rounded-xl"
                    >
                      {category.label} ({category.count})
                    </Button>
                  ))}
                </div>
                <p className="text-gray-500  mt-2 mb-0">
                  {ambassadorCategory === "WAITING_APPROVAL"
                    ? "Candidates who completed all lessons and qualifying quiz, waiting for final admin approval."
                    : ambassadorCategory === "PENDING"
                      ? "Candidates who submitted their application form and are currently in progress with lessons and verification."
                      : ambassadorCategory === "APPROVED"
                        ? "Active approved ambassadors with full access to promo tasks and live performance tracking."
                        : ambassadorCategory === "STUDENTS"
                          ? "View approved and pending ambassadors who provided student information."
                          : "View ambassadors who did not provide student information."}
                </p>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Input
                placeholder="Search ambassadors by name, email, phone or code..."
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="large"
                allowClear
                className="rounded-xl font-medium shadow-xs flex-1"
              />
              <Badge dot={statusFilter !== "ALL" || studentFilter !== "ALL" || pointsSort !== "NONE"}>
                <Button
                  type="primary"
                  icon={<FilterOutlined style={{ color: "#ffffff", fontSize: "16px" }} />}
                  style={{ backgroundColor: "#2563eb", borderColor: "#2563eb" }}
                  className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  title="Filter & Sort Ambassadors"
                  onClick={() => setFilterDrawerVisible(true)}
                />
              </Badge>
              <Button
                type="default"
                icon={<ReloadOutlined style={{ fontSize: "16px" }} />}
                onClick={fetchAmbassadors}
                className="h-10 w-10 rounded-xl flex items-center justify-center border-gray-200 text-gray-700 hover:bg-gray-50 flex-shrink-0"
                title="Refresh Ambassadors"
              />
            </div>

            {filteredAmbassadors.length === 0 ? (
              <Empty
                description="No ambassadors found matching the filter"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button onClick={fetchAmbassadors} className="font-gilroyBold">
                  Refresh Data
                </Button>
              </Empty>
            ) : (
              <div className="space-y-4">
                <AdminCollapseList items={renderCollapseItems()} />

                {filteredAmbassadors.length > 0 && (
                  <div className="flex justify-center mt-6">
                    <Pagination
                      current={currentPage}
                      pageSize={pageSize}
                      total={filteredAmbassadors.length}
                      onChange={setCurrentPage}
                      onShowSizeChange={(_, size) => {
                        setPageSize(size);
                        setCurrentPage(1);
                      }}
                      showSizeChanger
                      showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} ambassadors`
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeMainTab === "POSTS" && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-gray-50 p-4 sm:p-5 rounded-2xl border border-gray-200">
              <div className="flex-1 min-w-0">
                <h3 className="font-gilroyBold text-lg sm:text-xl text-gray-900 m-0">
                  Ambassador Campaign Post Tasks ({postTemplatesList.length})
                </h3>
                <p className="text-gray-500   m-0 mt-1 leading-relaxed">
                  Create, edit, and set live campaign post tasks and upload instructions for ambassadors.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2.5 w-full md:w-auto flex-shrink-0">
                <Button
                  type="primary"
                  onClick={() => {
                    fetchSubmittedPosts();
                    setPostsModalVisible(true);
                  }}
                  style={{ backgroundColor: "#2563eb", borderColor: "#2563eb" }}
                  className="font-gilroyBold rounded-xl flex items-center justify-center gap-1.5 h-10 px-4 flex-1 sm:flex-none"
                >
                  Review Submitted Posts
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    handlePrepareNewTemplate();
                    setNextPostModalVisible(true);
                  }}
                  style={{ backgroundColor: "#000000", borderColor: "#000000" }}
                  className="font-gilroyBold bg-black hover:bg-gray-800 text-white rounded-xl flex items-center justify-center gap-1.5 h-10 px-4 flex-1 sm:flex-none"
                >
                  + Create New Task
                </Button>
              </div>
            </div>

            {postTemplatesList.length === 0 ? (
              <Empty
                description="No campaign post tasks created yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    handlePrepareNewTemplate();
                    setNextPostModalVisible(true);
                  }}
                  style={{ backgroundColor: "#000000", borderColor: "#000000" }}
                  className="font-gilroyBold bg-black hover:bg-gray-800 text-white rounded-xl"
                >
                  + Create First Campaign Task
                </Button>
              </Empty>
            ) : (
              <AdminCollapseList items={renderPostCollapseItems()} />
            )}
          </div>
        )}

        {activeMainTab === "PROMOS" && <AdminAmbassadorPromos />}
        {activeMainTab === "LESSONS" && <AdminAmbassadorLessons />}

        <AdminDetailsModal
          visible={detailsModalVisible}
          title={`Ambassador Details — ${selectedAmbassador?.firstName || ""} ${selectedAmbassador?.lastName || ""}`}
          status={modalStatus}
          sections={modalSections}
          onCancel={handleCloseDetails}
          footerActions={[
            modalPhone ? (
              <Button
                key="whatsapp"
                type="default"
                icon={<WhatsAppOutlined />}
                onClick={() => {
                  const clean = modalPhone.replace(/\D/g, "");
                  window.open(`https://wa.me/${clean}`, "_blank");
                }}
              >
                WhatsApp Chat
              </Button>
            ) : null,
            selectedAmbassador ? (
              <React.Fragment key="ambassador-actions">
                <Button
                  type="primary"
                  danger
                  icon={<CloseOutlined />}
                  loading={actionLoadingId === selectedAmbassador?.id}
                  onClick={async () => {
                    await handleReject(selectedAmbassador.id);
                    handleCloseDetails();
                  }}
                >
                  Reject & Remove
                </Button>
                {modalStatus === "APPROVED" && (
                  <Button
                    type="primary"
                    loading={actionLoadingId === selectedAmbassador?.id}
                    onClick={async () => {
                      await handleSuspend(selectedAmbassador.id);
                      handleCloseDetails();
                    }}
                    style={{ backgroundColor: "#d97706", borderColor: "#d97706" }}
                    className="font-gilroyBold text-white"
                  >
                    Suspend Ambassador
                  </Button>
                )}
                {modalStatus === "SUSPENDED" && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined style={{ color: "#ffffff" }} />}
                    loading={actionLoadingId === selectedAmbassador?.id}
                    onClick={async () => {
                      await handleUnsuspend(selectedAmbassador.id);
                      handleCloseDetails();
                    }}
                    style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                    className="font-gilroyBold text-white"
                  >
                    Unsuspend Ambassador
                  </Button>
                )}
                {(modalStatus === "PENDING" || modalStatus === "WAITING_APPROVAL" || modalStatus === "WAITING_FOR_APPROVAL") && (
                  <Button
                    type="primary"
                    icon={<CheckOutlined style={{ color: "#ffffff" }} />}
                    disabled={!selectedAmbassador?.trainingCompleted}
                    title={
                      !selectedAmbassador?.trainingCompleted
                        ? "Requires completed final quizzes"
                        : "Approve Ambassador"
                    }
                    loading={actionLoadingId === selectedAmbassador?.id}
                    onClick={async () => {
                      await handleApprove(selectedAmbassador.id);
                      handleCloseDetails();
                    }}
                    style={
                      !selectedAmbassador?.trainingCompleted
                        ? { opacity: 0.6 }
                        : { backgroundColor: "#16a34a", borderColor: "#16a34a" }
                    }
                    className="font-gilroyBold text-white"
                  >
                    Approve
                  </Button>
                )}
              </React.Fragment>
            ) : null,
          ].filter(Boolean)}
        />

        <Drawer
          title={<span className="font-gilroyBold text-xl">Manage Ambassador Schools List</span>}
          placement="bottom"
          height="85vh"
          open={schoolsModalVisible}
          onClose={() => setSchoolsModalVisible(false)}
          className="rounded-t-3xl"
          styles={{
            body: { overflowY: "auto", paddingBottom: "40px" },
            content: { display: "flex", flexDirection: "column", maxHeight: "85vh" },
          }}
          bodyStyle={{ overflowY: "auto", paddingBottom: "40px" }}
          footer={
            <div className="flex justify-end items-center gap-3 p-2">
              <Button onClick={() => setSchoolsModalVisible(false)} className="font-gilroyBold rounded-xl">
                Cancel
              </Button>
              <Button
                type="primary"
                loading={savingSchools}
                onClick={handleSaveSchools}
                style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                className="font-gilroyBold rounded-xl text-white border-0"
              >
                Save Schools List
              </Button>
            </div>
          }
        >
          <div style={{ maxHeight: "calc(85vh - 140px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} className="space-y-4 py-2 pr-1">
            <p className="text-gray-600">
              Add or remove higher institutions (Universities, Polytechnics, Colleges & Others) available for Ambassador applications and profiles.
            </p>

            <div className="flex gap-2">
              <Input
                placeholder="Enter new school name (e.g. Federal Polytechnic Ilaro)"
                value={newSchoolInput}
                onChange={(e) => setNewSchoolInput(e.target.value)}
                size="large"
                className="rounded-xl"
              />
              <Button
                type="primary"
                size="large"
                style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                className="rounded-xl font-gilroyBold"
                onClick={() => {
                  if (!newSchoolInput.trim()) return;
                  if (schoolsList.includes(newSchoolInput.trim())) {
                    showErrorNotification("School already exists in list.");
                    return;
                  }
                  setSchoolsList([...schoolsList, newSchoolInput.trim()]);
                  setNewSchoolInput("");
                }}
              >
                Add School
              </Button>
            </div>

            <div className="border border-gray-200 rounded-2xl p-4 max-h-96 overflow-y-auto space-y-2">
              {schoolsList.length === 0 ? (
                <p className="text-gray-400 text-center py-4">No schools in list</p>
              ) : (
                schoolsList.map((sch, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center bg-gray-50 p-3 rounded-xl border border-gray-100"
                  >
                    <span className="font-medium text-gray-800">{sch}</span>
                    {sch !== "Other" && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        onClick={() => {
                          setSchoolsList(schoolsList.filter((_, i) => i !== idx));
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </Drawer>

        <Drawer
          title={<span className="font-gilroyBold text-xl">Review Ambassador Submitted Posts</span>}
          placement="bottom"
          height="85vh"
          open={postsModalVisible}
          onClose={() => setPostsModalVisible(false)}
          className="rounded-t-3xl"
          styles={{
            body: { overflowY: "auto", paddingBottom: "40px" },
            content: { display: "flex", flexDirection: "column", maxHeight: "85vh" },
          }}
          bodyStyle={{ overflowY: "auto", paddingBottom: "40px" }}
          footer={
            <div className="flex justify-end items-center gap-3 p-2">
              <Button onClick={() => setPostsModalVisible(false)} className="font-gilroyBold rounded-xl">
                Close
              </Button>
            </div>
          }
        >
          <div style={{ maxHeight: "calc(85vh - 140px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} className="space-y-4 py-2 pr-1">
            <div className="flex justify-end items-center mb-2">
              <Button
                type="default"
                icon={<ReloadOutlined />}
                size="small"
                onClick={() => fetchSubmittedPosts("ALL")}
                className="rounded-lg font-gilroyBold"
              >
                Refresh
              </Button>
            </div>

            <Spin spinning={loadingPosts}>
              <div className="space-y-3 pr-1">
                {submittedPostsList.length === 0 ? (
                  <Empty description="No submitted posts found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                  submittedPostsList.map((post) => {
                    const ambassadorName = post.user ? `${post.user.firstName} ${post.user.lastName}` : "Ambassador";
                    const ambassadorEmail = post.user?.email || "";
                    const status = post.status || "PENDING";
                    const createdAt = post.createdAt ? new Date(post.createdAt).toLocaleString() : "—";

                    return (
                      <div
                        key={post.id}
                        className="p-4 bg-white border border-gray-200 rounded-2xl shadow-xs space-y-2 flex flex-col md:flex-row md:items-center justify-between gap-4"
                      >
                        <div className="space-y-1 overflow-hidden">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-900">{ambassadorName}</span>
                            <span className="text-gray-500">({ambassadorEmail})</span>
                            <Tag color={status === "APPROVED" ? "green" : status === "REJECTED" ? "red" : "gold"} className="font-gilroyBold">
                              {status}
                            </Tag>
                            <Tag color="blue" className="font-mono">{post.platform || "LINK"}</Tag>
                          </div>

                          <div>
                            <a
                              href={post.postUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 font-mono truncate block hover:underline "
                            >
                              {post.postUrl}
                            </a>
                          </div>

                          <div className="text-gray-400 ">
                            Submitted: {createdAt}
                            {post.adminNote && <span className="text-red-500 font-medium ml-2">Note: {post.adminNote}</span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <a
                            href={post.postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium border border-gray-200 "
                          >
                            Open Link
                          </a>

                          {status !== "APPROVED" && (
                            <Button
                              type="primary"
                              size="small"
                              icon={<CheckOutlined />}
                              loading={updatingPostId === post.id}
                              onClick={() => handleUpdatePostStatus(post.id, "APPROVED")}
                              style={{ backgroundColor: "#16a34a", borderColor: "#16a34a" }}
                              className="rounded-xl font-gilroyBold"
                            >
                              Approve
                            </Button>
                          )}

                          {status !== "REJECTED" && (
                            <Button
                              type="primary"
                              danger
                              size="small"
                              icon={<CloseOutlined />}
                              loading={updatingPostId === post.id}
                              onClick={() => handleUpdatePostStatus(post.id, "REJECTED")}
                              className="rounded-xl font-gilroyBold"
                            >
                              Reject
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </Spin>
          </div>
        </Drawer>

        {/* Manage Next Campaign Task Drawer */}
        <Drawer
          placement="bottom"
          open={nextPostModalVisible}
          onClose={() => setNextPostModalVisible(false)}
          height="90vh"
          className="rounded-t-3xl"
          styles={{
            body: { overflowY: "auto", paddingBottom: "40px" },
            content: { display: "flex", flexDirection: "column", maxHeight: "90vh" },
          }}
          bodyStyle={{ overflowY: "auto", paddingBottom: "40px" }}
          title={
            <span className="font-gilroyBold text-xl text-gray-900">
              Manage Ambassador Next Post & Captions
            </span>
          }
          footer={
            <div className="flex justify-end items-center gap-3 p-2">
              <Button
                onClick={() => setNextPostModalVisible(false)}
                className="font-gilroyBold  h-12 px-6 rounded-2xl"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                loading={savingNextPost}
                onClick={handleSaveNextPost}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-gilroyBold  h-12 px-6 rounded-2xl shadow-lg shadow-emerald-600/25 border-0 cursor-pointer"
              >
                Save & Publish Task
              </Button>
            </div>
          }
        >
          <div style={{ maxHeight: "calc(90vh - 140px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} className="space-y-6 md:space-y-7 py-3 text-left pr-2 pb-12">
            {/* Created Post Tasks History List */}
            <div className="p-4 bg-gray-50 border border-gray-200/80 rounded-2xl space-y-3 mb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-gilroyBold text-gray-900 ">
                  Created Campaign Tasks ({createdPostTemplates.length})
                </span>
                <Button
                  type="primary"
                  size="small"
                  onClick={handlePrepareNewTemplate}
                  className="bg-black hover:bg-gray-800 font-gilroyBold rounded-xl  px-4 cursor-pointer border-0"
                >
                  + Create New Task
                </Button>
              </div>

              {loadingTemplates ? (
                <div className="text-center py-3"><Spin size="small" /></div>
              ) : createdPostTemplates.length === 0 ? (
                <p className="text-gray-400 font-medium my-1 ">No past created tasks found.</p>
              ) : (
                <div className="space-y-2.5 max-h-52 overflow-y-auto pr-1">
                  {createdPostTemplates.map((item) => (
                    <div
                      key={item.id}
                      className={`p-3 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 bg-white ${nextPostData.id === item.id ? "border-black ring-1 ring-black" : "border-gray-200"
                        }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-gilroyBold text-gray-900  truncate">{item.title}</span>
                          <Tag color={item.isActive ? "green" : "default"} className="font-gilroyBold text-[10px] uppercase m-0">
                            {item.isActive ? "LIVE ACTIVE" : "INACTIVE"}
                          </Tag>
                          <Tag className="font-mono text-[10px] m-0">{item.mediaType}</Tag>
                        </div>
                        {item.updatedAt && (
                          <span className="text-[10px] text-gray-400 font-mono block mt-0.5">
                            Updated: {new Date(item.updatedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-auto">
                        <Button
                          size="small"
                          type="default"
                          onClick={() => handleSelectTemplateForEdit(item)}
                          className="font-gilroyBold text-[11px] rounded-lg"
                        >
                          Edit
                        </Button>
                        {!item.isActive && (
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => handleSetActiveTemplate(item.id)}
                            className="bg-emerald-600 hover:bg-emerald-500 font-gilroyBold text-[11px] rounded-lg border-0"
                          >
                            Set Live
                          </Button>
                        )}
                        <Button
                          size="small"
                          danger
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() => handleDeleteTemplate(item.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="block font-gilroyBold text-gray-900  mb-2">
                Task Title
              </label>
              <Input
                value={nextPostData.title}
                onChange={(e) => setNextPostData({ ...nextPostData, title: e.target.value })}
                placeholder="e.g. Next Campaign Post"
                className="rounded-xl py-3 px-4 "
              />
            </div>

            <div className="space-y-2">
              <label className="block font-gilroyBold text-gray-900 mb-2">
                Media Type
              </label>
              <select
                value={nextPostData.mediaType}
                onChange={(e) => setNextPostData({ ...nextPostData, mediaType: e.target.value })}
                className="w-full py-3 px-4 border border-gray-300 rounded-xl bg-white font-gilroyBold text-gray-800 outline-none focus:border-emerald-500"
              >
                <option value="IMAGE">Image</option>
                <option value="VIDEO">Video</option>
              </select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block font-gilroyBold text-gray-900  mb-0.5">
                    Media Asset Attachment
                  </label>
                  <p className=" text-gray-500 font-gilroyRegular mb-0">
                    Upload images/videos or paste direct asset links
                  </p>
                </div>
                <Tag color={nextPostData.mediaType === "VIDEO" ? "purple" : "blue"} className="font-gilroyBold  uppercase px-2.5 py-0.5 rounded-full m-0">
                  {nextPostData.mediaType || "IMAGE"} MODE
                </Tag>
              </div>

              {/* Drag & Drop Upload Zone + Inline URL Bar */}
              <div className="space-y-3">
                {/* Drag and Drop Zone */}
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDraggingMedia(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDraggingMedia(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDraggingMedia(false);
                    const files = Array.from(e.dataTransfer.files || []);
                    files.forEach(handleFileUpload);
                  }}
                  className={`relative border-2 border-dashed rounded-2xl p-5 text-center transition-all duration-200 cursor-pointer ${isDraggingMedia
                    ? "border-emerald-500 bg-emerald-50/60 shadow-md scale-[1.01]"
                    : "border-slate-300 hover:border-emerald-500 hover:bg-emerald-50/20 bg-slate-50/60"
                    }`}
                >
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10"
                    disabled={uploadingMediaFile}
                    onChange={(e) => {
                      Array.from(e.target.files || []).forEach(handleFileUpload);
                      e.target.value = "";
                    }}
                  />
                  <div className="flex flex-col items-center justify-center space-y-2 pointer-events-none">
                    <div className="w-12 h-12 rounded-2xl bg-emerald-100/80 text-emerald-600 flex items-center justify-center shadow-xs">
                      {uploadingMediaFile ? (
                        <Spin size="medium" />
                      ) : (
                        <CloudUploadOutlined className="text-2xl text-emerald-600" />
                      )}
                    </div>
                    <div>
                      <span className="font-gilroyBold text-slate-800 ">
                        {uploadingMediaFile ? "Uploading Media Asset..." : "Click to upload files"}
                      </span>
                      <span className="text-slate-500  font-gilroyRegular"> or drag & drop assets here</span>
                    </div>
                    <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                      <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-600">
                        JPG, PNG, WEBP
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] font-mono text-slate-600">
                        MP4, MOV, WEBM
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[11px] font-gilroyBold">
                        Max 50MB
                      </span>
                    </div>
                  </div>
                </div>

                {/* URL Input Bar Divider */}
                <div className="relative flex py-1 items-center">
                  <div className="flex-grow border-t border-slate-200"></div>
                  <span className="flex-shrink mx-3 text-[11px] uppercase font-gilroyBold text-slate-400 tracking-wider">
                    or paste direct link
                  </span>
                  <div className="flex-grow border-t border-slate-200"></div>
                </div>

                {/* URL Input Field */}
                <div className="relative">
                  <Input
                    prefix={<LinkOutlined className="text-slate-400 mr-1" />}
                    value={nextPostData.mediaUrl}
                    onChange={(e) => {
                      const val = e.target.value;
                      const isVideo = val.endsWith(".mp4") || val.endsWith(".mov") || val.endsWith(".webm");
                      setNextPostData({
                        ...nextPostData,
                        mediaUrl: val,
                        mediaUrls: val ? Array.from(new Set([val, ...(nextPostData.mediaUrls || [])])) : nextPostData.mediaUrls,
                        mediaType: isVideo ? "VIDEO" : (nextPostData.mediaType || "IMAGE"),
                      });
                    }}
                    placeholder="Paste image or video URL (https://minio-api...)"
                    className="rounded-xl py-3 px-4  w-full font-mono bg-white border-slate-300 focus:border-emerald-500 shadow-xs"
                  />
                </div>
              </div>

              {/* Media Preview Container */}
              {(nextPostData.mediaUrls?.length > 0 || nextPostData.mediaUrl) && (
                <div className="p-4 sm:p-5 bg-slate-900 text-white rounded-2xl space-y-4 shadow-lg border border-slate-800">
                  <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                        <PictureOutlined className="text-base" />
                      </div>
                      <div>
                        <h4 className="text-white font-gilroyBold  tracking-wide mb-0 uppercase">
                          Media Preview
                        </h4>
                        <p className="text-[11px] text-slate-400 font-gilroyRegular mb-0">
                          Attachment preview for campaign post
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Tag color="purple" className="font-mono  m-0 border-0 bg-purple-500/20 text-purple-300 font-gilroyBold">
                        {nextPostData.mediaType || "IMAGE"}
                      </Tag>
                      <Tag color="emerald" className="font-gilroyBold  m-0 border-0 bg-emerald-500/20 text-emerald-400">
                        {(nextPostData.mediaUrls?.length || (nextPostData.mediaUrl ? 1 : 0))} Asset{(nextPostData.mediaUrls?.length > 1 ? "s" : "")}
                      </Tag>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {(nextPostData.mediaUrls?.length ? nextPostData.mediaUrls : [nextPostData.mediaUrl]).filter(Boolean).map((url, idx) => {
                      const isVid = nextPostData.mediaType === "VIDEO" || url.endsWith(".mp4") || url.endsWith(".mov") || url.endsWith(".webm");
                      return (
                        <div key={url || idx} className="group relative bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 hover:border-slate-700 transition-all shadow-md flex flex-col">
                          {/* Top Bar Badges & Remove Button */}
                          <div className="absolute top-2.5 left-2.5 right-2.5 z-20 flex items-center justify-between pointer-events-none">
                            <span className="bg-slate-900/90 backdrop-blur-md text-emerald-400 text-[10px] font-gilroyBold px-2.5 py-1 rounded-full border border-emerald-500/30 flex items-center gap-1 shadow-xs pointer-events-auto">
                              <CheckCircleOutlined />
                              {idx === 0 ? "PRIMARY ASSET" : `ASSET #${idx + 1}`}
                            </span>

                            <div className="flex items-center gap-1.5 pointer-events-auto">
                              <button
                                type="button"
                                onClick={() => {
                                  if (navigator.clipboard) {
                                    navigator.clipboard.writeText(url);
                                    showSuccessNotification("Copied media URL to clipboard!");
                                  }
                                }}
                                title="Copy URL"
                                className="w-8 h-8 rounded-full bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white backdrop-blur-md transition-all flex items-center justify-center border border-slate-700 shadow-xs cursor-pointer"
                              >
                                <CopyOutlined className="" />
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  const updatedList = (nextPostData.mediaUrls || []).filter((item) => item !== url);
                                  setNextPostData({
                                    ...nextPostData,
                                    mediaUrls: updatedList,
                                    mediaUrl: updatedList[0] || "",
                                  });
                                }}
                                title="Remove asset"
                                className="w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-600 text-white backdrop-blur-md transition-all flex items-center justify-center border border-red-400/50 shadow-xs cursor-pointer"
                              >
                                <DeleteOutlined className="" />
                              </button>
                            </div>
                          </div>

                          {/* Media Viewer Area */}
                          <div className="relative aspect-video bg-black flex items-center justify-center p-2 overflow-hidden">
                            {isVid ? (
                              <video src={url} controls className="w-full h-full object-contain rounded-xl" />
                            ) : (
                              <img
                                src={url}
                                alt={`Asset ${idx + 1}`}
                                className="w-full h-full object-contain rounded-xl group-hover:scale-105 transition-transform duration-300"
                              />
                            )}
                          </div>

                          {/* Footer URL Snippet */}
                          <div className="p-3 bg-slate-900/90 border-t border-slate-800 flex items-center justify-between  gap-2">
                            <div className="flex items-center gap-2 truncate">
                              {isVid ? (
                                <VideoCameraOutlined className="text-purple-400 flex-shrink-0" />
                              ) : (
                                <FileImageOutlined className="text-emerald-400 flex-shrink-0" />
                              )}
                              <span className="font-mono text-slate-400 text-[11px] truncate">{url}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block font-gilroyBold text-gray-900 mb-2 leading-normal">
                  Channel Captions (Use <code className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded font-mono inline-block my-0.5">&#123;REFERRAL_LINK&#125;</code> placeholder)
                </label>
                <Checkbox
                  checked={nextPostData.useSameCaption !== false}
                  onChange={(e) => setNextPostData((current) => ({
                    ...current,
                    useSameCaption: e.target.checked,
                    captions: e.target.checked ? {} : Object.fromEntries(
                      CAPTION_CHANNELS.map(({ key }) => [key, current.captions?.[key] || current.caption || DEFAULT_CAPTION])
                    ),
                  }))}
                >
                  Use the same caption everywhere
                </Checkbox>
              </div>

              {nextPostData.useSameCaption !== false ? (
                <Input.TextArea
                  rows={4}
                  value={nextPostData.caption}
                  onChange={(e) => setNextPostData({ ...nextPostData, caption: e.target.value })}
                  placeholder="Write the shared caption... e.g. Join using my link: {REFERRAL_LINK}"
                  className="rounded-xl font-mono leading-relaxed border-gray-200 p-3"
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {CAPTION_CHANNELS.map(({ key, label }) => (
                    <div key={key}>
                      <label className="block font-gilroyBold text-gray-800 mb-2">{label} caption</label>
                      <Input.TextArea
                        rows={4}
                        value={nextPostData.captions?.[key] || ""}
                        onChange={(e) => setNextPostData((current) => ({
                          ...current,
                          captions: { ...current.captions, [key]: e.target.value },
                          caption: key === "WHATSAPP" ? e.target.value : current.caption,
                        }))}
                        placeholder={`Write the ${label} caption...`}
                        className="rounded-xl font-mono leading-relaxed border-gray-200 p-3"
                      />
                    </div>
                  ))}
                </div>
              )}
              <p className="text-gray-500 mt-2 leading-normal m-0">
                The ambassador's unique referral link automatically replaces &#123;REFERRAL_LINK&#125; in every caption. WhatsApp is for status sharing; no public WhatsApp URL is required.
              </p>
            </div>

            <div className="space-y-2">
              <label className="block font-gilroyBold text-gray-900 mb-2">
                How to Upload (Step-by-step Instructions for Ambassadors)
              </label>
              <Input.TextArea
                rows={4}
                value={nextPostData.instructions}
                onChange={(e) => setNextPostData({ ...nextPostData, instructions: e.target.value })}
                placeholder="1. Download the media asset&#10;2. Copy caption with your referral link&#10;3. Share on Instagram/TikTok/WhatsApp Status&#10;4. Submit post link for verification"
                className="rounded-xl leading-relaxed border-gray-200 p-3"
              />
            </div>
          </div>
        </Drawer>

        <Drawer
          title={
            <div className="flex items-center gap-2">
              <span className="font-gilroyBold text-xl">{selectedPostTemplateDetails?.title || "Campaign Task Details"}</span>
              {selectedPostTemplateDetails?.isActive && (
                <Tag color="green" className="font-gilroyBold uppercase">LIVE ACTIVE</Tag>
              )}
            </div>
          }
          placement="bottom"
          height="85vh"
          open={postDetailsModalVisible}
          onClose={() => setPostDetailsModalVisible(false)}
          className="rounded-t-3xl"
          footer={
            <div className="flex justify-end items-center gap-3 p-2">
              <Button key="close" onClick={() => setPostDetailsModalVisible(false)} className="font-gilroyBold rounded-xl">
                Close
              </Button>
              <Button
                key="edit"
                type="primary"
                onClick={() => {
                  setPostDetailsModalVisible(false);
                  if (selectedPostTemplateDetails) {
                    handleSelectTemplateForEdit(selectedPostTemplateDetails);
                    setNextPostModalVisible(true);
                  }
                }}
                style={{ backgroundColor: "#000000", borderColor: "#000000" }}
                className="font-gilroyBold bg-black text-white rounded-xl border-0"
              >
                Edit Task
              </Button>
            </div>
          }
        >
          {selectedPostTemplateDetails && (
            <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-1.5">
              {selectedPostTemplateDetails.mediaUrl && (
                <div className="p-4 bg-slate-900 text-white rounded-2xl border border-slate-800 space-y-3 shadow-xs">
                  <div className="flex items-center justify-between">
                    <span className="font-gilroyBold  uppercase tracking-wider text-slate-300 flex items-center gap-2">
                      <PictureOutlined className="text-emerald-400 " />
                      Media Asset Attachment
                    </span>
                    <Tag color="purple" className="font-mono  m-0 border-0 bg-purple-500/20 text-purple-300 font-gilroyBold">
                      {selectedPostTemplateDetails.mediaType || "IMAGE"}
                    </Tag>
                  </div>
                  <div className="relative aspect-video bg-slate-950 rounded-xl overflow-hidden flex items-center justify-center border border-slate-800">
                    {selectedPostTemplateDetails.mediaType === "VIDEO" || selectedPostTemplateDetails.mediaUrl.endsWith(".mp4") ? (
                      <video src={selectedPostTemplateDetails.mediaUrl} controls className="w-full h-full object-contain" />
                    ) : (
                      <Image src={selectedPostTemplateDetails.mediaUrl} alt="Asset" className="w-full h-full object-contain p-2" />
                    )}
                  </div>
                </div>
              )}

              {selectedPostTemplateDetails.caption && (
                <div>
                  <label className="block font-gilroyBold text-gray-500 uppercase mb-1">
                    Post Caption (with &#123;REFERRAL_LINK&#125;)
                  </label>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 font-mono text-gray-800 whitespace-pre-line">
                    {selectedPostTemplateDetails.caption}
                  </div>
                </div>
              )}

              {selectedPostTemplateDetails.instructions && (
                <div>
                  <label className="block font-gilroyBold text-gray-500 uppercase mb-1">
                    How to Upload (Step-by-step Instructions for Ambassadors)
                  </label>
                  <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 text-gray-700 whitespace-pre-line leading-relaxed">
                    {selectedPostTemplateDetails.instructions}
                  </div>
                </div>
              )}
            </div>
          )}
        </Drawer>

        <Drawer
          title={<span className="font-gilroyBold text-xl">Filter & Sort Ambassadors</span>}
          placement="bottom"
          height="auto"
          open={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          className="rounded-t-3xl"
        >
          <div className="space-y-6 max-w-lg mx-auto pb-6">
            <div>
              <h4 className="font-gilroyBold text-gray-700 uppercase mb-2">Ambassador Status</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  type={statusFilter === "ALL" ? "primary" : "default"}
                  onClick={() => setStatusFilter("ALL")}
                  className="font-gilroyBold rounded-xl"
                >
                  All Status ({ambassadors.length})
                </Button>
                <Button
                  type={statusFilter === "APPROVED" ? "primary" : "default"}
                  onClick={() => setStatusFilter("APPROVED")}
                  className="font-gilroyBold rounded-xl"
                >
                  Active ({approvedCount})
                </Button>
                <Button
                  type={statusFilter === "PENDING" ? "primary" : "default"}
                  onClick={() => setStatusFilter("PENDING")}
                  className="font-gilroyBold rounded-xl"
                >
                  Pending ({pendingCount})
                </Button>
              </div>
            </div>

            <div>
              <h4 className="font-gilroyBold text-gray-700 uppercase mb-2">Audience Category</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  type={studentFilter === "ALL" ? "primary" : "default"}
                  onClick={() => setStudentFilter("ALL")}
                  className="font-gilroyBold rounded-xl"
                >
                  All Audience ({ambassadors.length})
                </Button>
                <Button
                  type={studentFilter === "STUDENT" ? "primary" : "default"}
                  onClick={() => setStudentFilter("STUDENT")}
                  className="font-gilroyBold rounded-xl"
                >
                  Students ({ambassadors.filter((a) => Boolean(a.school && a.school !== "—") || a.isStudent === true || String(a.isStudent) === "1" || String(a.isStudent) === "true").length})
                </Button>
                <Button
                  type={studentFilter === "NON_STUDENT" ? "primary" : "default"}
                  onClick={() => setStudentFilter("NON_STUDENT")}
                  className="font-gilroyBold rounded-xl"
                >
                  Non-Students ({ambassadors.filter((a) => !a.school && !a.isStudent && String(a.isStudent) !== "1" && String(a.isStudent) !== "true").length})
                </Button>
              </div>
            </div>

            <div>
              <h4 className="font-gilroyBold text-gray-700 uppercase mb-2">Sort By Points</h4>
              <div className="flex flex-wrap gap-2">
                <Button
                  type={pointsSort === "NONE" ? "primary" : "default"}
                  onClick={() => setPointsSort("NONE")}
                  className="font-gilroyBold rounded-xl"
                >
                  Default Order
                </Button>
                <Button
                  type={pointsSort === "POINTS_DESC" ? "primary" : "default"}
                  onClick={() => setPointsSort("POINTS_DESC")}
                  className="font-gilroyBold rounded-xl"
                >
                  Points: High → Low
                </Button>
                <Button
                  type={pointsSort === "POINTS_ASC" ? "primary" : "default"}
                  onClick={() => setPointsSort("POINTS_ASC")}
                  className="font-gilroyBold rounded-xl"
                >
                  Points: Low → High
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
              <Button
                className="flex-1 rounded-xl font-gilroyBold h-12"
                onClick={() => {
                  setStatusFilter("ALL");
                  setStudentFilter("ALL");
                  setPointsSort("NONE");
                }}
              >
                Reset Filters
              </Button>
              <Button
                type="primary"
                className="flex-1 rounded-xl font-gilroyBold h-12 bg-blue-600"
                onClick={() => setFilterDrawerVisible(false)}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </Drawer>
      </div>
    </Spin>
  );
};

export default AdminAmbassadors;
